import { useState, useEffect, useRef } from 'react';
import { Building2, CheckCircle2, AlertCircle, Plus } from 'lucide-react';
import { Button } from '../../ui/button';
import { plaidApi } from '../../../../services/api';
import PlaidLinkButton from '../../../Plaid/PlaidLinkButton';
import { formatCurrency } from '../InvestmentFlow.utils';

// Plaid account shape — matches backend GET /plaid/accounts & POST /plaid/exchange-token
interface PlaidAccount {
  id: string;
  account_name: string;
  last4?: string;
  account_type: string;
  subtype: string;
  institution_name?: string;
}

interface PaymentStepProps {
  investmentAmount: number;
  selectedAccountId: string | null;
  onAccountSelect: (id: string) => void;
  onSubmit: () => void;
  canSubmit: boolean;
}

export function PaymentStep({
  investmentAmount,
  selectedAccountId,
  onAccountSelect,
  onSubmit,
  canSubmit,
}: PaymentStepProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [accounts, setAccounts] = useState<PlaidAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  useEffect(() => {
    let cancelled = false;

    plaidApi
      .getAccounts()
      .then((response) => {
        if (cancelled) return;
        console.log('🏦 getAccounts raw response:', JSON.stringify(response.data));
        if (response.data?.success) {
          const accts = (response.data.data?.accounts as PlaidAccount[]) ?? [];
          console.log('🏦 loaded accounts:', accts.map(a => ({ id: a.id, name: a.account_name })));
          setAccounts(accts);
        } else {
          setFetchError('Failed to load your bank accounts');
        }
      })
      .catch((err) => {
        console.error('🏦 getAccounts error:', err);
        if (!cancelled) setFetchError('Failed to load your bank accounts');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  function handleAccountLinked(newAccounts: PlaidAccount[]) {
    console.log('🏦 handleAccountLinked: received', newAccounts.length, 'accounts', newAccounts.map(a => ({ id: a.id, name: a.account_name })));
    setAccounts((prev) => [...prev, ...newAccounts]);
    if (newAccounts.length > 0) {
      console.log('🏦 selecting account:', newAccounts[0].id);
      onAccountSelect(newAccounts[0].id);
    }
  }

  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-3xl md:text-4xl focus-visible:outline-none"
        >
          Choose a bank account
        </h1>
        <p className="text-lg text-muted-foreground">
          Funding {formatCurrency(investmentAmount)} via ACH transfer · Free · 3–5 business days
        </p>
      </div>

      {/* Account list */}
      {isLoading ? (
        <div className="space-y-3" aria-busy="true" aria-label="Loading bank accounts">
          <div className="h-20 rounded-xl bg-secondary animate-pulse" />
          <div className="h-20 rounded-xl bg-secondary animate-pulse" />
        </div>
      ) : fetchError ? (
        <div
          role="alert"
          className="flex items-center gap-3 p-4 rounded-lg bg-destructive/10 text-destructive"
        >
          <AlertCircle className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
          <p className="text-sm">{fetchError}</p>
        </div>
      ) : (
        <div role="radiogroup" aria-label="Bank accounts" className="space-y-3">
          {accounts.map((account) => {
            const isSelected = selectedAccountId === account.id;
            return (
              <button
                key={account.id}
                type="button"
                role="radio"
                aria-checked={isSelected}
                onClick={() => onAccountSelect(account.id)}
                className={`w-full p-5 rounded-xl border-2 text-left transition-all ${
                  isSelected
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50 hover:bg-accent'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-primary" aria-hidden="true" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium capitalize">
                      {account.subtype} ···{account.last4}
                    </p>
                    <p className="text-sm text-muted-foreground truncate">{account.account_name}</p>
                  </div>
                  {isSelected && (
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" aria-hidden="true" />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Link a bank account */}
      {!isLoading && !fetchError && (
        <PlaidLinkButton
          onSuccess={handleAccountLinked}
          variant="outline"
          className="w-full h-12 justify-center"
        >
          <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
          {accounts.length === 0 ? 'Link a bank account' : 'Add another account'}
        </PlaidLinkButton>
      )}

      <Button
        onClick={onSubmit}
        disabled={!canSubmit}
        className="w-full h-14 text-lg disabled:opacity-40"
      >
        Invest {formatCurrency(investmentAmount)}
      </Button>
    </div>
  );
}
